"use client";

import Link from "next/link";
import CheckIcon from "@/components/icons/CheckIcon";
import Button from "@/components/ui/Button";
import YouTubeFacade from "@/components/ui/YouTubeFacade";

const HIGHLIGHTS = [
  "Premium THC and CBD products for every user",
  "Easy online ordering with same-day delivery",
  "Best prices in Alberta for any budget",
  "Expert danktenders + live chat support",
] as const;

const DEFAULT_BODY =
  "Dank's dispensaries offer the best service and selection in Calgary. We carry " +
  "cannabis products for every type of user-from exotic flower and high-end edibles " +
  "to high-potency dabs. Order online for same-day pickup or delivery, or visit our " +
  "cannabis store for one-on-one help from our expert danktenders.";

export type CalgaryBestDispensaryCta = {
  label: string;
  href: string;
};

export type CalgaryBestDispensarySectionProps = {
  heading?: string;
  body?: string;
  highlights?: readonly string[];
  primaryCta?: CalgaryBestDispensaryCta;
  secondaryCta?: CalgaryBestDispensaryCta | null;
  embedSrc?: string;
  videoTitle?: string;
  headingId?: string;
  posterSrc?: string;
};

export default function CalgaryBestDispensarySection({
  heading = "Calgary's best cannabis dispensary & Alberta delivery",
  body = DEFAULT_BODY,
  highlights = HIGHLIGHTS,
  primaryCta = { label: "Same day delivery", href: "/search" },
  secondaryCta = { label: "Alberta mail order", href: "/search?delivery=mail-order" },
  embedSrc = "https://www.youtube.com/embed/YOUR_VIDEO_ID?rel=0&modestbranding=1&showinfo=0",
  videoTitle = "Dank Cannabis dispensary - Calgary store tour",
  headingId = "calgary-best-dispensary-heading",
  // No poster by default, so the facade draws the stand-in. This was a stock
  // product shot borrowed from a homepage banner, which made a slot that is
  // still waiting on a store tour look finished. The embed above it is
  // likewise still YOUR_VIDEO_ID.
  posterSrc = "",
}: CalgaryBestDispensarySectionProps = {}) {
  return (
    <section className="w-full max-w-none" aria-labelledby={headingId}>
      <div className="w-full">
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-10">
          <div className="min-w-0">
            <h2
              id={headingId}
              className="text-h3-mobile font-bold leading-tight text-blue-80 sm:text-h3-desktop"
            >
              {heading}
            </h2>

            <p className="mt-4 text-body-sm leading-relaxed text-text-secondary sm:text-body-md">
              {body}
            </p>

            <ul className="mt-5 flex flex-col gap-2.5">
              {highlights.map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-blue-80 text-constant-white">
                    <CheckIcon size={12} color="currentColor" />
                  </span>
                  <span className="text-body-sm font-medium text-text-primary sm:text-body-md">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="brand" size="lg" className="w-full sm:w-auto rounded-xl px-6 py-3">
                <Link href={primaryCta.href}>
                  {primaryCta.label}
                </Link>
              </Button>
              {secondaryCta ? (
                <Button asChild variant="brand" size="lg" className="w-full sm:w-auto rounded-xl px-6 py-3">
                  <Link href={secondaryCta.href}>
                    {secondaryCta.label}
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>

          <div className="relative aspect-[644/375] w-full overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white p-2 shadow">
            <div className="relative h-full w-full overflow-hidden rounded-xl">
              <YouTubeFacade
                src={embedSrc}
                title={videoTitle}
                poster={posterSrc}
                posterAlt={videoTitle}
                iframeClassName="absolute inset-0 h-full w-full"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
